import { Search, X, cn } from "@app/ui";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { motion, springs, useReducedMotion } from "../../../lib/motion/index.js";
import { useDismiss } from "../../../lib/useDismiss.js";
import { useI18n } from "../../../lib/useI18n.js";
import type { EmojiEntry } from "./emoji-data.js";
import { loadEmojiIndex, normalizeQuery, type EmojiIndex } from "./emoji-index.js";

const PANEL_WIDTH = 352;
const PANEL_MAX_HEIGHT = 380;
const GAP = 6;
/** Cap search results so a broad query ("a") can't mount ~2k buttons at once. */
const MAX_RESULTS = 400;

interface Props {
  /** Rect of the trigger button, so the panel can anchor to it. */
  anchor: DOMRect;
  /** Current emoji, if any — enables the inline "remove" affordance. */
  current?: string | null | undefined;
  /** Entity name, for accessible labels. */
  name: string;
  onPick: (emoji: string) => void;
  /** Clear the emoji (called for the remove affordance). */
  onRemove?: () => void;
  onClose: () => void;
}

/** Floating emoji picker anchored to a topic's emoji button. A search box (any
 *  language — matches keywords from every emojibase locale) filters the whole
 *  standard emoji set; with no query it shows a curated 3×10 quick-pick grid
 *  followed by every category. The (large, all-language) dataset loads as its
 *  own lazy chunk, from which unrenderable glyphs are dropped. Positioned +
 *  dismissed like {@link SnoozeMenu}. */
export function EmojiPicker({ anchor, current, name, onPick, onRemove, onClose }: Props) {
  const { t, language } = useI18n();
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [index, setIndex] = useState<EmojiIndex | null>(null);
  // Paint the quick-pick grid first, then mount the (much larger) full category
  // list a frame later — so opening feels instant instead of waiting on ~1.7k
  // buttons to reconcile at once.
  const [showCategories, setShowCategories] = useState(false);

  useDismiss(onClose, { capture: true }, ref);

  useEffect(() => {
    let alive = true;
    void loadEmojiIndex().then((i) => { if (alive) setIndex(i); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!index) return;
    const id = requestAnimationFrame(() => setShowCategories(true));
    return () => cancelAnimationFrame(id);
  }, [index]);

  // Flip above the anchor when there isn't room below; clamp to the viewport.
  // Recomputed once the dataset loads, since the panel grows to full height then.
  useLayoutEffect(() => {
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 16);
    const height = ref.current?.offsetHeight ?? PANEL_MAX_HEIGHT;
    const below = anchor.bottom + GAP;
    const top = below + height <= window.innerHeight - 8 ? below : Math.max(8, anchor.top - GAP - height);
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
    setPos({ left, top });
  }, [anchor, index, showCategories]);

  const results = useMemo(() => {
    if (!index) return null;
    const q = normalizeQuery(query).trim();
    if (!q) return null;
    const terms = q.split(/\s+/);
    const keys = index.keywords();
    const out: EmojiEntry[] = [];
    for (let i = 0; i < index.data.length; i++) {
      const k = keys[i]!;
      if (terms.every((term) => k.includes(term))) {
        out.push(index.data[i]!);
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  }, [query, index]);

  function pick(emoji: string) {
    onPick(emoji);
    onClose();
  }

  const width = Math.min(PANEL_WIDTH, typeof window === "undefined" ? PANEL_WIDTH : window.innerWidth - 16);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={t("emoji.pickNamed", { name })}
      style={{ position: "fixed", left: pos?.left ?? -9999, top: pos?.top ?? -9999, width, zIndex: 9999 }}
    >
      <motion.div
        initial={reduce ? false : { opacity: 0, scale: 0.97, y: -4 }}
        animate={reduce ? {} : { opacity: 1, scale: 1, y: 0 }}
        transition={springs.snappy}
        className="flex flex-col overflow-hidden rounded-lg border bg-card/95 text-card-foreground shadow-lg backdrop-blur-sm"
        style={{ maxHeight: PANEL_MAX_HEIGHT }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-2 py-1.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results && results[0]) { e.preventDefault(); pick(results[0][0]); }
            }}
            placeholder={t("emoji.search")}
            aria-label={t("emoji.search")}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {current && onRemove && (
            <button
              type="button"
              onClick={() => { onRemove(); onClose(); }}
              title={t("emoji.remove")}
              aria-label={t("emoji.remove")}
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2"
          // One delegated handler for the whole grid — avoids ~1.7k per-button
          // click closures, which measurably speeds up the initial mount.
          onClick={(e) => {
            const el = (e.target as HTMLElement).closest<HTMLElement>("[data-emoji]");
            if (el?.dataset.emoji) pick(el.dataset.emoji);
          }}
        >
          {!index ? (
            <p className="px-1 py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : results ? (
            results.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-muted-foreground">{t("emoji.none")}</p>
            ) : (
              <Grid emojis={results.map((e) => e[0])} />
            )
          ) : (
            <>
              <Section title={t("emoji.frequent")}>
                <Grid emojis={index.frequent} />
              </Section>
              {showCategories &&
                index.byGroup.map((entries, gi) => (
                  <Section key={index.groups[gi]!.key} title={language === "de" ? index.groups[gi]!.de : index.groups[gi]!.en}>
                    <Grid emojis={entries.map((e) => e[0])} defer />
                  </Section>
                ))}
            </>
          )}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <p className="sticky top-0 z-10 bg-card/95 px-1 py-1 text-[calc(11px*var(--app-text-scale))] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
        {title}
      </p>
      {children}
    </div>
  );
}

/** A grid of emoji cells. Clicks bubble to the scroll container's delegated
 *  handler (each cell just carries `data-emoji`). `defer` marks a large,
 *  off-screen category so the browser can skip its layout/paint until scrolled. */
function Grid({ emojis, defer }: { emojis: string[]; defer?: boolean }) {
  return (
    <div
      className="grid grid-cols-10 gap-0.5"
      style={defer ? { contentVisibility: "auto", containIntrinsicSize: "auto 240px" } : undefined}
    >
      {emojis.map((emoji) => (
        <button
          key={emoji}
          type="button"
          data-emoji={emoji}
          className={cn("flex aspect-square items-center justify-center rounded text-xl leading-none hover:bg-accent")}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}
